import { useState } from 'react'
import {X, UserPlus, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import { COUNTRIES } from '../utils/countries'
import { resendInvitationEmail, type EventForInvitation } from '../utils/resendInvitation'

interface Props {
  event: EventForInvitation
  onClose: () => void
  onCreated: () => void
}

const generateConfirmationToken = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return `${Date.now()}${Math.random().toString(36).slice(2)}`
}

// Verifie si un vernissage a venir est programme pour cette exposition, afin de
// proposer (comme sur la page publique) la case "participera au vernissage".
const isVernissageUpcoming = (event: EventForInvitation): boolean => {
  if (!event.vernissageDate) return false
  const vDate = new Date(event.vernissageDate)
  if (isNaN(vDate.getTime())) return false
  const vDay = new Date(vDate.getFullYear(), vDate.getMonth(), vDate.getDate())
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return vDay >= today
}

// Formulaire d'ajout manuel d'un invite depuis l'admin : cree l'entree
// event_invitees (comme le formulaire public) puis declenche l'envoi de
// l'e-mail d'invitation via la fonction Deno sendEventInvitation.
export default function ManualInviteForm({ event, onClose, onCreated }: Props) {
  const vernissageAvailable = isVernissageUpcoming(event)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: 'Suisse',
    language: 'fr' as 'fr' | 'en',
    attendingVernissage: false,
    newsletterOptIn: false,
    sendEmail: true,
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Merci de renseigner le prénom et le nom.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Merci de renseigner une adresse e-mail valide.')
      return
    }

    setSubmitting(true)
    const now = new Date().toISOString()
    const confirmationToken = generateConfirmationToken()
    const safeEmail = form.email.trim().toLowerCase()

    try {
      await lumi.entities.event_invitees.create({
        eventId: event._id,
        eventTitle: event.title,
        email: safeEmail,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        country: form.country,
        language: form.language,
        newsletterOptIn: form.newsletterOptIn,
        attendingVernissage: form.attendingVernissage,
        confirmationToken,
        status: 'invite',
        invitedAt: now,
        creator: 'admin_manual',
        creatorEmail: safeEmail,
        createdAt: now,
        updatedAt: now,
      })

      if (form.sendEmail) {
        try {
          await resendInvitationEmail(event, {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: safeEmail,
            country: form.country,
            language: form.language,
            attendingVernissage: form.attendingVernissage,
            confirmationToken,
            newsletterOptIn: form.newsletterOptIn,
          })
          toast.success(`Invité ajouté et invitation envoyée à ${safeEmail}`)
        } catch (mailError: any) {
          console.warn('Invité ajouté mais e-mail non envoyé:', mailError?.data?.error || mailError)
          toast.error("Invité ajouté, mais l'envoi de l'e-mail a échoué")
        }
      } else {
        toast.success('Invité ajouté (sans envoi d\'e-mail)')
      }

      onCreated()
      onClose()
    } catch (err: any) {
      console.error("Erreur lors de l'ajout manuel de l'invité:", err)
      setError(err?.data?.error || 'Une erreur est survenue. Merci de réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-xl border border-[#D4C5B9]/40 w-full max-w-lg p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 text-[#2C2C2C]/50 hover:text-[#722F37] transition-colors"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <UserPlus size={18} className="text-[#722F37]" />
          <h3 className="font-['EB_Garamond'] text-2xl font-bold text-[#2C2C2C]">
            Invitation manuelle
          </h3>
        </div>
        <p className="text-sm text-[#2C2C2C]/60 mb-6">
          Ajoutez un invité manuellement pour l'exposition « {event.title} ». Il sera enregistré
          exactement comme via le formulaire public.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-1.5">Prénom *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                className="w-full px-3.5 py-2.5 border border-[#D4C5B9]/60 rounded-lg outline-none focus:border-[#8B7355]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-1.5">Nom *</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                className="w-full px-3.5 py-2.5 border border-[#D4C5B9]/60 rounded-lg outline-none focus:border-[#8B7355]"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-[#2C2C2C] mb-1.5">E-mail *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3.5 py-2.5 border border-[#D4C5B9]/60 rounded-lg outline-none focus:border-[#8B7355]"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-1.5">Pays</label>
              <select
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
                className="w-full px-3.5 py-2.5 border border-[#D4C5B9]/60 rounded-lg outline-none focus:border-[#8B7355] bg-white"
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-1.5">
                Langue de l'invitation
              </label>
              <select
                value={form.language}
                onChange={(e) => setForm((p) => ({ ...p, language: e.target.value as 'fr' | 'en' }))}
                className="w-full px-3.5 py-2.5 border border-[#D4C5B9]/60 rounded-lg outline-none focus:border-[#8B7355] bg-white"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <div className="flex items-start gap-3 mb-4">
            <input
              type="checkbox"
              id="manual-send-email"
              checked={form.sendEmail}
              onChange={(e) => setForm((p) => ({ ...p, sendEmail: e.target.checked }))}
              className="mt-1 w-4 h-4 accent-[#8C6D3F] border-[#D4C5B9] rounded focus:outline-none focus:ring-2 focus:ring-[#8B7355] cursor-pointer"
            />
            <label htmlFor="manual-send-email" className="text-sm text-[#2C2C2C]/70 leading-relaxed cursor-pointer">
              Envoyer immédiatement l'e-mail d'invitation à cet invité.
            </label>
          </div>

          {vernissageAvailable ? (
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                id="manual-attending-vernissage"
                checked={form.attendingVernissage}
                onChange={(e) => setForm((p) => ({ ...p, attendingVernissage: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#8C6D3F] border-[#D4C5B9] rounded focus:outline-none focus:ring-2 focus:ring-[#8B7355] cursor-pointer"
              />
              <label htmlFor="manual-attending-vernissage" className="text-sm text-[#2C2C2C]/70 leading-relaxed cursor-pointer">
                Participera au vernissage.
              </label>
            </div>
          ) : null}

          <div className="flex items-start gap-3 mb-6">
            <input
              type="checkbox"
              id="manual-newsletter-optin"
              checked={form.newsletterOptIn}
              onChange={(e) => setForm((p) => ({ ...p, newsletterOptIn: e.target.checked }))}
              className="mt-1 w-4 h-4 accent-[#8C6D3F] border-[#D4C5B9] rounded focus:outline-none focus:ring-2 focus:ring-[#8B7355] cursor-pointer"
            />
            <label htmlFor="manual-newsletter-optin" className="text-sm text-[#2C2C2C]/70 leading-relaxed cursor-pointer">
              Inscrire également cet invité à la newsletter Amanaliya Art.
            </label>
          </div>

          {error ? (
            <p className="text-sm text-red-600 mb-4" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-[#D4C5B9]/60 text-[#2C2C2C] rounded-lg hover:bg-[#FAF9F6] transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#722F37] text-white rounded-lg hover:bg-[#5e2730] transition-colors font-medium disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              {submitting ? 'Ajout en cours...' : "Ajouter l'invité"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
